/**
 * `/audience` (§16.2 Audience console, §12.5): participant profiles (masked), segments builder with server-side
 * preview, opted-in exports with purpose + watermark + expiry + access log, engagement task results, follows
 * and joins driven, winners messaged per platform. Tabs are routes so each view is linkable.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EngagementResultsDto, ParticipantRowDto, SegmentDto, SegmentRule } from '@windrop/contracts';
import { SocialPlatform } from '@windrop/contracts';
import { Button, Field, Input, Modal, Select, useIntl, useToast } from '@windrop/ui';
import { useMemo, useState, type ReactNode } from 'react';
import { Link, Outlet } from 'react-router';
import { Can } from '../../auth/guards';
import { useStepUp, StepUpCancelled } from '../../auth/step-up';
import { Callout } from '../../components/Callout';
import { BarsChart } from '../../components/Chart';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState, NoDataYet } from '../../components/EmptyState';
import { FilterBar, FilterField, FilterSelect } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { audienceApi, exportsApi } from '../../lib/api/analytics';
import { isApiError } from '../../lib/api/client';

export const AUDIENCE_TABS = [
  { id: '', label: 'Profiles' },
  { id: 'segments', label: 'Segments' },
  { id: 'exports', label: 'Exports' },
  { id: 'engagement', label: 'Engagement' },
] as const;

export function AudiencePage() {
  return (
    <>
      <PageHeader
        title="Audience"
        breadcrumb={[{ label: 'Audience' }]}
        description="One profile per participant across platforms. Lists are masked by default; exports contain only opted-in contacts and every download is logged."
        tabs={AUDIENCE_TABS.map((t) => ({ id: t.id, label: t.label }))}
        tabsBase="/audience"
      />
      <Outlet />
    </>
  );
}

const consentTone = (s: ParticipantRowDto['marketing']) =>
  s === 'OPTED_IN' ? 'success' : s === 'WITHDRAWN' ? 'warning' : 'neutral';

export function AudienceProfilesTab() {
  const intl = useIntl();
  const [consent, setConsent] = useState('');
  const [platform, setPlatform] = useState('');
  const [q, setQ] = useState('');
  const query = useQuery({
    queryKey: ['audience', 'participants', { consent, platform, q }],
    queryFn: () =>
      audienceApi.participants({
        consent: consent ? (consent as ParticipantRowDto['marketing']) : undefined,
        platform: platform ? (platform as SocialPlatform) : undefined,
        q: q || undefined,
        limit: 100,
      }),
  });
  const columns = useMemo<Column<ParticipantRowDto>[]>(
    () => [
      {
        id: 'name',
        header: 'Participant',
        sortValue: (r) => r.displayName.toLowerCase(),
        cell: (r) => (
          <>
            <Link to={`/audience/participants/${r.participantId}`}>
              <strong>{r.displayName}</strong>
            </Link>
            <br />
            <code className="wd-muted">{r.participantId}</code>
          </>
        ),
      },
      { id: 'contact', header: 'Contact (masked)', cell: (r) => <code>{r.contactMasked}</code> },
      {
        id: 'platform',
        header: 'Source platform',
        sortValue: (r) => r.sourcePlatform ?? 'DIRECT',
        cell: (r) => humanizeStatus(r.sourcePlatform ?? 'DIRECT'),
      },
      {
        id: 'lotteries',
        header: 'Lotteries',
        align: 'end',
        sortValue: (r) => r.lotteriesEntered,
        cell: (r) => intl.formatNumber(r.lotteriesEntered),
      },
      {
        id: 'prizes',
        header: 'Prizes won',
        align: 'end',
        sortValue: (r) => r.prizesWon,
        cell: (r) => intl.formatNumber(r.prizesWon),
      },
      {
        id: 'marketing',
        header: 'Marketing consent',
        sortValue: (r) => r.marketing,
        cell: (r) => <ToneBadge tone={consentTone(r.marketing)}>{humanizeStatus(r.marketing)}</ToneBadge>,
      },
      {
        id: 'created',
        header: 'First seen',
        sortValue: (r) => r.createdAt,
        cell: (r) => intl.formatRelative(r.createdAt),
      },
    ],
    [intl],
  );
  return (
    <>
      <FilterBar>
        <FilterField label="Consent state" htmlFor="consent-filter">
          <FilterSelect
            id="consent-filter"
            data-testid="consent-filter"
            value={consent}
            onChange={setConsent}
            options={[
              { value: '', label: 'Any' },
              { value: 'OPTED_IN', label: 'Opted in' },
              { value: 'WITHDRAWN', label: 'Withdrawn' },
              { value: 'NONE', label: 'No consent' },
            ]}
          />
        </FilterField>
        <FilterField label="Source platform" htmlFor="platform-filter">
          <FilterSelect
            id="platform-filter"
            value={platform}
            onChange={setPlatform}
            options={[
              { value: '', label: 'Any' },
              ...SocialPlatform.options.map((p) => ({ value: p, label: humanizeStatus(p) })),
            ]}
          />
        </FilterField>
        <FilterField label="Participant id or name" htmlFor="participant-q" grow>
          <input
            id="participant-q"
            className="wd-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="01ARZ… or Ada"
          />
        </FilterField>
      </FilterBar>
      {query.isError ? (
        <ErrorState
          title="Could not load participants"
          body="Retry, or check the API status."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : (
        <DataTable
          caption="Participants (masked)"
          columns={columns}
          rows={query.data?.items ?? []}
          rowKey={(r) => r.participantId}
          loading={query.isPending}
          emptyState={
            <EmptyState
              title="No participants match"
              body="Entries create profiles automatically."
              action={<Button href="/lotteries">Open lotteries</Button>}
            />
          }
        />
      )}
    </>
  );
}

// --- segments ---------------------------------------------------------------------------------------------

const RULE_KINDS: { kind: SegmentRule['kind']; label: string }[] = [
  { kind: 'ENTERED_AT_LEAST', label: 'Entered at least N lotteries' },
  { kind: 'WON_PRIZE_CATEGORY', label: 'Won a prize in category' },
  { kind: 'SOURCE_PLATFORM', label: 'Came from platform' },
  { kind: 'ENTERED_LOTTERY', label: 'Entered a specific lottery' },
  { kind: 'VARIANT_PREFERENCE', label: 'Chose a variant option' },
  { kind: 'CONSENT_STATE', label: 'Marketing consent state' },
];

const defaultRule = (kind: SegmentRule['kind']): SegmentRule => {
  switch (kind) {
    case 'ENTERED_AT_LEAST':
      return { kind, lotteries: 2 };
    case 'WON_PRIZE_CATEGORY':
      return { kind, category: 'phones' };
    case 'SOURCE_PLATFORM':
      return { kind, platform: 'INSTAGRAM' };
    case 'ENTERED_LOTTERY':
      return { kind, lotteryId: '' };
    case 'VARIANT_PREFERENCE':
      return { kind, axis: 'colour', value: '' };
    case 'CONSENT_STATE':
      return { kind, state: 'OPTED_IN' };
  }
};

export const describeRule = (r: SegmentRule): string => {
  switch (r.kind) {
    case 'ENTERED_AT_LEAST':
      return `Entered ${r.lotteries}+ lotteries`;
    case 'WON_PRIZE_CATEGORY':
      return `Won a ${r.category}`;
    case 'SOURCE_PLATFORM':
      return `Came from ${humanizeStatus(r.platform)}`;
    case 'ENTERED_LOTTERY':
      return `Entered lottery ${r.lotteryId}`;
    case 'VARIANT_PREFERENCE':
      return `Chose ${r.axis} = ${r.value}`;
    case 'CONSENT_STATE':
      return `Consent ${humanizeStatus(r.state)}`;
  }
};

function RuleEditor({
  rule,
  onChange,
  onRemove,
}: {
  rule: SegmentRule;
  onChange: (r: SegmentRule) => void;
  onRemove: () => void;
}) {
  const field = (label: string, control: ReactNode) => (
    <Field label={label} id={`rule-${label}`}>
      {control}
    </Field>
  );
  return (
    <li className="wdc-form-grid wdc-fieldset">
      <Field label="Rule" id="rule-kind">
        <Select
          value={rule.kind}
          onChange={(e) => onChange(defaultRule(e.target.value as SegmentRule['kind']))}
          options={RULE_KINDS.map((k) => ({ value: k.kind, label: k.label }))}
        />
      </Field>
      {rule.kind === 'ENTERED_AT_LEAST'
        ? field(
            'Lotteries',
            <Input
              type="number"
              min={1}
              value={rule.lotteries}
              onChange={(e) => onChange({ ...rule, lotteries: Number(e.target.value) || 1 })}
            />,
          )
        : null}
      {rule.kind === 'WON_PRIZE_CATEGORY'
        ? field(
            'Category',
            <Input value={rule.category} onChange={(e) => onChange({ ...rule, category: e.target.value })} />,
          )
        : null}
      {rule.kind === 'SOURCE_PLATFORM'
        ? field(
            'Platform',
            <Select
              value={rule.platform}
              onChange={(e) => onChange({ ...rule, platform: e.target.value as SocialPlatform })}
              options={SocialPlatform.options.map((p) => ({ value: p, label: humanizeStatus(p) }))}
            />,
          )
        : null}
      {rule.kind === 'ENTERED_LOTTERY'
        ? field(
            'Lottery id',
            <Input
              value={rule.lotteryId}
              onChange={(e) => onChange({ ...rule, lotteryId: e.target.value })}
            />,
          )
        : null}
      {rule.kind === 'VARIANT_PREFERENCE' ? (
        <>
          {field(
            'Axis',
            <Input value={rule.axis} onChange={(e) => onChange({ ...rule, axis: e.target.value })} />,
          )}
          {field(
            'Value',
            <Input value={rule.value} onChange={(e) => onChange({ ...rule, value: e.target.value })} />,
          )}
        </>
      ) : null}
      {rule.kind === 'CONSENT_STATE'
        ? field(
            'State',
            <Select
              value={rule.state}
              onChange={(e) =>
                onChange({ ...rule, state: e.target.value as 'OPTED_IN' | 'WITHDRAWN' | 'NONE' })
              }
              options={[
                { value: 'OPTED_IN', label: 'Opted in' },
                { value: 'WITHDRAWN', label: 'Withdrawn' },
                { value: 'NONE', label: 'No consent' },
              ]}
            />,
          )
        : null}
      <Button variant="quiet" onClick={onRemove}>
        Remove
      </Button>
    </li>
  );
}

export function SegmentBuilder({
  initial,
  onSaved,
}: {
  initial?: SegmentDto;
  onSaved?: (s: SegmentDto) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(initial?.name ?? '');
  const [rules, setRules] = useState<SegmentRule[]>(initial?.rules ?? [defaultRule('SOURCE_PLATFORM')]);
  const save = useMutation({
    mutationFn: () => audienceApi.upsertSegment({ segmentId: initial?.segmentId, name, rules }),
    onSuccess: (s) => {
      void queryClient.invalidateQueries({ queryKey: ['audience', 'segments'] });
      toast.toast({
        title: `Segment saved — ${s.optedInCount} opted-in of ${s.totalMatched} matched`,
        tone: 'success',
      });
      onSaved?.(s);
    },
  });
  return (
    <form
      className="wdc-panel"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      aria-label={initial ? 'Edit segment' : 'New segment'}
    >
      <Field label="Segment name" id="segment-name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <ul className="wdc-list" data-gap="sm">
        {rules.map((r, i) => (
          <RuleEditor
            key={i}
            rule={r}
            onChange={(nr) => setRules(rules.map((x, j) => (j === i ? nr : x)))}
            onRemove={() => setRules(rules.filter((_, j) => j !== i))}
          />
        ))}
      </ul>
      <div className="wd-btn-group">
        <Button variant="secondary" onClick={() => setRules([...rules, defaultRule('ENTERED_AT_LEAST')])}>
          Add rule
        </Button>
        <Button
          type="submit"
          loading={save.isPending}
          disabled={!name || rules.length === 0}
          disabledReason="A name and at least one rule are required"
        >
          Save segment
        </Button>
      </div>
      <p className="wd-muted">
        All rules must match (AND). Counts are evaluated on the server; exports only ever contain opted-in
        contacts.
      </p>
      {save.isError ? <p role="alert">{(save.error as Error).message}</p> : null}
    </form>
  );
}

export function ExportDialog({
  segment,
  open,
  onOpenChange,
}: {
  segment: SegmentDto;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [purpose, setPurpose] = useState('');
  const [note, setNote] = useState('');
  const [format, setFormat] = useState<'CSV' | 'JSON'>('CSV');
  const [ack, setAck] = useState(false);
  const { withStepUp } = useStepUp();
  const queryClient = useQueryClient();
  const toast = useToast();
  const run = useMutation({
    mutationFn: () =>
      withStepUp(
        () =>
          audienceApi.export({
            segmentId: segment.segmentId,
            purpose: purpose as 'CAMPAIGN' | 'PRODUCT_LAUNCH' | 'CRM_SYNC',
            format,
            purposeNote: note || undefined,
            acknowledgeOptInOnly: true,
          }),
        { permission: 'audience.export', reason: `Exporting segment ${segment.name}` },
      ),
    onSuccess: (job) => {
      void queryClient.invalidateQueries({ queryKey: ['exports'] });
      toast.toast({
        title: `Export queued · watermark ${job.watermark}`,
        description: job.expiresAt ? `Expires ${new Date(job.expiresAt).toLocaleString()}` : undefined,
        tone: 'success',
      });
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof StepUpCancelled) return;
    },
  });
  const ready = Boolean(purpose) && ack;
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Export "${segment.name}"`}
      description={`${segment.optedInCount} opted-in contacts of ${segment.totalMatched} matched. Withdrawn and non-opted-in contacts are never included.`}
      actions={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => run.mutate()}
            loading={run.isPending}
            disabled={!ready}
            disabledReason="Choose a purpose and acknowledge the opt-in rule"
          >
            Request export
          </Button>
        </>
      }
    >
      <div className="wd-stack">
        <Field
          label="Purpose"
          id="export-purpose"
          required
          hint="Recorded with the export history and the watermark."
        >
          <Select
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="Choose a purpose"
            options={[
              { value: 'CAMPAIGN', label: 'Campaign' },
              { value: 'PRODUCT_LAUNCH', label: 'Product launch' },
              { value: 'CRM_SYNC', label: 'CRM sync' },
            ]}
          />
        </Field>
        <Field label="Purpose note" id="export-note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Spring launch mailing" />
        </Field>
        <Field label="Format" id="export-format">
          <Select
            value={format}
            onChange={(e) => setFormat(e.target.value as 'CSV' | 'JSON')}
            options={[
              { value: 'CSV', label: 'CSV' },
              { value: 'JSON', label: 'JSON' },
            ]}
          />
        </Field>
        <label className="wdc-check-list__item">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            data-testid="export-ack"
          />
          <span>
            I understand the file is watermarked, access-logged, expires in 24 hours and contains opted-in
            contacts only.
          </span>
        </label>
        {run.isError && !(run.error instanceof StepUpCancelled) ? (
          <p role="alert">{(run.error as Error).message}</p>
        ) : null}
      </div>
    </Modal>
  );
}

export function AudienceSegmentsTab() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['audience', 'segments'], queryFn: audienceApi.segments });
  const [exporting, setExporting] = useState<SegmentDto | null>(null);
  const [editing, setEditing] = useState<SegmentDto | null | 'new'>(null);
  const preview = useMutation({
    mutationFn: (id: string) => audienceApi.preview(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['audience', 'segments'] }),
  });
  return (
    <div className="wd-stack">
      {query.isError ? (
        <ErrorState
          title="Could not load segments"
          body="Retry in a moment."
          action={<Button onClick={() => query.refetch()}>Retry</Button>}
        />
      ) : query.isPending ? (
        <NoDataYet what="segments" hint="Loading…" />
      ) : query.data.length === 0 && editing === null ? (
        <EmptyState
          title="No segments yet"
          body='Build one such as "entered 2+ lotteries", "won a phone" or "came from Instagram".'
          action={<Button onClick={() => setEditing('new')}>New segment</Button>}
        />
      ) : (
        <ul className="wdc-list">
          {query.data?.map((s) => (
            <li key={s.segmentId} className="wdc-panel">
              <div className="wdc-panel__head">
                <div>
                  <h3>{s.name}</h3>
                  <p className="wd-muted">{s.rules.map(describeRule).join(' · ')}</p>
                  <p>
                    <strong className="wd-tabular">{intl.formatNumber(s.optedInCount)}</strong> opted-in of{' '}
                    <span className="wd-tabular">{intl.formatNumber(s.totalMatched)}</span> matched ·
                    evaluated {intl.formatRelative(s.updatedAt)}
                  </p>
                </div>
                <div className="wd-btn-group">
                  <Button
                    variant="secondary"
                    onClick={() => preview.mutate(s.segmentId)}
                    loading={preview.isPending && preview.variables === s.segmentId}
                  >
                    Refresh counts
                  </Button>
                  <Can permission="audience.export">
                    <Button variant="secondary" onClick={() => setEditing(s)}>
                      Edit
                    </Button>
                    <Button onClick={() => setExporting(s)}>Export opted-in</Button>
                  </Can>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Can permission="audience.export">
        {editing === null ? (
          query.data && query.data.length > 0 ? (
            <div>
              <Button variant="secondary" onClick={() => setEditing('new')}>
                New segment
              </Button>
            </div>
          ) : null
        ) : (
          <SegmentBuilder
            initial={editing === 'new' ? undefined : editing}
            onSaved={() => setEditing(null)}
          />
        )}
      </Can>
      {exporting ? (
        <ExportDialog segment={exporting} open onOpenChange={(o) => (o ? null : setExporting(null))} />
      ) : null}
    </div>
  );
}

// --- exports ----------------------------------------------------------------------------------------------

export function AudienceExportsTab() {
  const intl = useIntl();
  const toast = useToast();
  const query = useQuery({ queryKey: ['exports'], queryFn: () => exportsApi.list(), refetchInterval: 5000 });
  const download = useMutation({
    mutationFn: (id: string) => exportsApi.download(id),
    onSuccess: (d) => {
      window.open(d.downloadUrl, '_blank', 'noopener');
      toast.toast({
        title: 'Download logged',
        description: `Watermark ${d.watermark}; the link expires in 5 minutes.`,
        tone: 'neutral',
      });
    },
  });
  const rows = query.data?.items ?? [];
  return (
    <>
      <Callout tone="info">
        <p>
          Every export is watermarked and time-limited; each download is written to the access log and the
          audit trail (§16.3).
        </p>
      </Callout>
      <DataTable
        caption="Export jobs"
        rows={rows}
        rowKey={(r) => r.exportId}
        loading={query.isPending}
        emptyState={
          <EmptyState
            title="No exports yet"
            body="Request one from a segment or a lottery."
            action={<Button href="/audience/segments">Open segments</Button>}
          />
        }
        columns={[
          { id: 'kind', header: 'Kind', sortValue: (r) => r.kind, cell: (r) => humanizeStatus(r.kind) },
          {
            id: 'status',
            header: 'Status',
            sortValue: (r) => r.status,
            cell: (r) => (
              <ToneBadge
                tone={
                  r.status === 'READY'
                    ? 'success'
                    : r.status === 'FAILED'
                      ? 'error'
                      : r.status === 'EXPIRED'
                        ? 'warning'
                        : 'info'
                }
              >
                {humanizeStatus(r.status)}
              </ToneBadge>
            ),
          },
          { id: 'purpose', header: 'Purpose', cell: (r) => r.purpose ?? '—' },
          {
            id: 'rows',
            header: 'Rows',
            align: 'end',
            cell: (r) =>
              r.rows === null
                ? '—'
                : `${intl.formatNumber(r.rows)}${r.suppressedCount ? ` (${intl.formatNumber(r.suppressedCount)} suppressed)` : ''}`,
          },
          { id: 'watermark', header: 'Watermark', cell: (r) => <code>{r.watermark}</code> },
          {
            id: 'expires',
            header: 'Expires',
            sortValue: (r) => r.expiresAt,
            cell: (r) => (r.expiresAt ? intl.formatDateTime(r.expiresAt) : '—'),
          },
          {
            id: 'created',
            header: 'Requested',
            sortValue: (r) => r.createdAt,
            cell: (r) => intl.formatRelative(r.createdAt),
          },
        ]}
        rowActions={(r) =>
          r.status === 'READY' ? (
            <Button
              variant="secondary"
              onClick={() => download.mutate(r.exportId)}
              loading={download.isPending && download.variables === r.exportId}
            >
              Download (logged)
            </Button>
          ) : null
        }
        initialSort={{ columnId: 'created', direction: 'desc' }}
      />
    </>
  );
}

// --- engagement -------------------------------------------------------------------------------------------

export function AudienceEngagementTab() {
  const intl = useIntl();
  const query = useQuery({ queryKey: ['audience', 'engagement'], queryFn: () => audienceApi.engagement() });
  const d: EngagementResultsDto | undefined = query.data;
  return (
    <div className="wd-stack">
      <div className="wdc-stats">
        <StatCard
          label="Follows and joins driven"
          loading={query.isPending}
          value={d ? intl.formatNumber(d.followsAndJoins) : null}
          hint="Qualified follow / link-channel tasks (attested, not API-verified)"
          emptyHint="Appears with the first task completion."
        />
        <StatCard
          label="Qualified referrals"
          loading={query.isPending}
          value={d ? intl.formatNumber(d.referrals.converted) : null}
          hint={
            d
              ? `${intl.formatNumber(d.referrals.total)} referrals recorded · raw link clicks earn nothing`
              : undefined
          }
          emptyHint="Appears with the first referral."
        />
        <StatCard
          label="Shares"
          loading={query.isPending}
          value={d ? intl.formatNumber(d.shares.links) : null}
          hint={
            d
              ? `${intl.formatNumber(d.shares.clicks)} clicks · ${intl.formatNumber(d.shares.conversions)} conversions`
              : undefined
          }
          emptyHint="Appears with the first share link."
        />
      </div>
      <BarsChart
        title="Winners messaged per platform"
        description="Hand-off state of every winner by the platform they came from (§12.5)."
        data={(d?.winnersByPlatform ?? []).map((w) => ({
          platform: humanizeStatus(w.platform),
          notMessaged: w.notMessaged,
          buttonTapped: w.buttonTapped,
          messaged: w.messaged + w.followedUp,
        }))}
        xKey="platform"
        xLabel="Platform"
        series={[
          { key: 'messaged', label: 'Messaged', color: 'success' },
          { key: 'buttonTapped', label: 'Button tapped', color: 'warning' },
          { key: 'notMessaged', label: 'Not messaged', color: 'error' },
        ]}
        emptyHint="Appears once winners exist."
      />
      <DataTable
        caption="Engagement task results"
        rows={d?.tasks ?? []}
        rowKey={(t) => t.taskId}
        loading={query.isPending}
        pageSize={0}
        emptyState={
          <EmptyState
            title="No engagement tasks yet"
            body="Tasks are configured per lottery in the wizard."
            action={<Button href="/lotteries">Open lotteries</Button>}
          />
        }
        columns={[
          {
            id: 'title',
            header: 'Task',
            sortValue: (t) => t.title,
            cell: (t) => (
              <>
                <strong>{t.title}</strong>
                <br />
                <span className="wd-muted">
                  {humanizeStatus(t.type)}
                  {t.platform ? ` · ${humanizeStatus(t.platform)}` : ''}
                </span>
              </>
            ),
          },
          {
            id: 'completions',
            header: 'Completions',
            align: 'end',
            sortValue: (t) => t.completions,
            cell: (t) => intl.formatNumber(t.completions),
          },
          {
            id: 'qualified',
            header: 'Qualified',
            align: 'end',
            sortValue: (t) => t.qualified,
            cell: (t) => intl.formatNumber(t.qualified),
          },
          { id: 'pending', header: 'Pending', align: 'end', cell: (t) => intl.formatNumber(t.pending) },
          { id: 'rejected', header: 'Rejected', align: 'end', cell: (t) => intl.formatNumber(t.rejected) },
          {
            id: 'bonus',
            header: 'Bonus entries',
            align: 'end',
            sortValue: (t) => t.bonusEntries,
            cell: (t) => intl.formatNumber(t.bonusEntries),
          },
        ]}
      />
    </div>
  );
}
